import React, { useState } from 'react';
import { X } from 'lucide-react';
import { LocalMatch, CreateLocalMatchRequest, UpdateLocalMatchRequest, LocalMatchStatus, LocalMatchday } from '../../types/localMatchday';

interface LocalMatchFormProps {
  match?: LocalMatch;
  matchdays?: LocalMatchday[];
  onSave: (data: CreateLocalMatchRequest | UpdateLocalMatchRequest) => Promise<void>;
  onCancel: () => void;
  loading?: boolean;
}

const LocalMatchForm: React.FC<LocalMatchFormProps> = ({
  match,
  matchdays = [],
  onSave,
  onCancel,
  loading = false
}) => {
  const [formData, setFormData] = useState({
    homeTeam: match?.homeTeam || '',
    awayTeam: match?.awayTeam || '',
    homeScore: match?.homeScore?.toString() || '',
    awayScore: match?.awayScore?.toString() || '',
    scheduledDate: match?.scheduledDate ? new Date(match.scheduledDate).toISOString().slice(0, 16) : '',
    actualDate: match?.actualDate ? new Date(match.actualDate).toISOString().slice(0, 16) : '',
    location: match?.location || '',
    referee: match?.referee || '',
    duration: match?.duration?.toString() || '',
    notes: match?.notes || '',
    status: match?.status?.toString() || LocalMatchStatus.Scheduled.toString(),
    isActive: match?.isActive ?? true,
    localMatchdayId: match?.localMatchdayId?.toString() || (matchdays.length > 0 ? matchdays[0].id.toString() : '')
  });

  const [errors, setErrors] = useState<Record<string, string>>({});

  const validateForm = () => {
    const newErrors: Record<string, string> = {};

    if (!formData.homeTeam.trim()) {
      newErrors.homeTeam = 'Heimteam ist erforderlich';
    }
    if (!formData.awayTeam.trim()) {
      newErrors.awayTeam = 'Auswärtsteam ist erforderlich';
    }
    if (!formData.scheduledDate) {
      newErrors.scheduledDate = 'Geplantes Datum ist erforderlich';
    }
    if (matchdays.length > 0 && !match && !formData.localMatchdayId) {
      newErrors.localMatchdayId = 'Spieltag ist erforderlich';
    }
    if (formData.homeScore && isNaN(Number(formData.homeScore))) {
      newErrors.homeScore = 'Ungültiges Ergebnis';
    }
    if (formData.awayScore && isNaN(Number(formData.awayScore))) {
      newErrors.awayScore = 'Ungültiges Ergebnis';
    }
    if (formData.duration && isNaN(Number(formData.duration))) {
      newErrors.duration = 'Ungültige Dauer';
    }

    setErrors(newErrors);
    return Object.keys(newErrors).length === 0;
  };

  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault();
    
    if (!validateForm()) {
      return;
    }

    const data = {
      homeTeam: formData.homeTeam.trim(),
      awayTeam: formData.awayTeam.trim(),
      homeScore: formData.homeScore ? Number(formData.homeScore) : undefined,
      awayScore: formData.awayScore ? Number(formData.awayScore) : undefined,
      scheduledDate: new Date(formData.scheduledDate).toISOString(),
      actualDate: formData.actualDate ? new Date(formData.actualDate).toISOString() : undefined,
      location: formData.location.trim() || undefined,
      referee: formData.referee.trim() || undefined,
      duration: formData.duration ? Number(formData.duration) : undefined,
      notes: formData.notes.trim() || undefined,
      status: Number(formData.status),
      isActive: formData.isActive,
      ...(formData.localMatchdayId && { localMatchdayId: Number(formData.localMatchdayId) })
    };

    await onSave(data);
  };

  const handleChange = (field: string, value: any) => {
    setFormData(prev => ({ ...prev, [field]: value }));
    if (errors[field]) {
      setErrors(prev => ({ ...prev, [field]: '' }));
    }
  };

  return (
    <div style={overlayStyle}>
      <div style={modalStyle}>
        <div style={headerStyle}>
          <h2>{match ? 'Spiel bearbeiten' : 'Neues Spiel erstellen'}</h2>
          <button onClick={onCancel} style={closeButtonStyle}><X size={16} /></button>
        </div>

        <form onSubmit={handleSubmit} style={formStyle}>
          {/* Spieltag Selection - only show if matchdays are provided and not editing */}
          {matchdays.length > 0 && !match && (
            <div style={fieldStyle}>
              <label style={labelStyle}>Spieltag *</label>
              <select
                value={formData.localMatchdayId}
                onChange={(e) => handleChange('localMatchdayId', e.target.value)}
                style={inputStyle}
              >
                <option value="">Spieltag auswählen</option>
                {matchdays.map((matchday) => (
                  <option key={matchday.id} value={matchday.id}>
                    {matchday.name} - {new Date(matchday.date).toLocaleDateString()}
                  </option>
                ))}
              </select>
              {errors.localMatchdayId && <span style={errorStyle}>{errors.localMatchdayId}</span>}
            </div>
          )}

          <div style={rowStyle}>
            <div style={fieldStyle}>
              <label style={labelStyle}>Heimteam *</label>
              <input
                type="text"
                value={formData.homeTeam}
                onChange={(e) => handleChange('homeTeam', e.target.value)}
                style={inputStyle}
                placeholder="Name des Heimteams"
              />
              {errors.homeTeam && <span style={errorStyle}>{errors.homeTeam}</span>}
            </div>

            <div style={fieldStyle}>
              <label style={labelStyle}>Auswärtsteam *</label>
              <input
                type="text"
                value={formData.awayTeam}
                onChange={(e) => handleChange('awayTeam', e.target.value)}
                style={inputStyle}
                placeholder="Name des Auswärtsteams"
              />
              {errors.awayTeam && <span style={errorStyle}>{errors.awayTeam}</span>}
            </div>
          </div>

          <div style={rowStyle}>
            <div style={fieldStyle}>
              <label style={labelStyle}>Heimergebnis</label>
              <input
                type="number"
                value={formData.homeScore}
                onChange={(e) => handleChange('homeScore', e.target.value)}
                style={inputStyle}
                placeholder="0"
                min="0"
              />
              {errors.homeScore && <span style={errorStyle}>{errors.homeScore}</span>}
            </div>

            <div style={fieldStyle}>
              <label style={labelStyle}>Auswärtsergebnis</label>
              <input
                type="number"
                value={formData.awayScore}
                onChange={(e) => handleChange('awayScore', e.target.value)}
                style={inputStyle}
                placeholder="0"
                min="0"
              />
              {errors.awayScore && <span style={errorStyle}>{errors.awayScore}</span>}
            </div>
          </div>

          <div style={rowStyle}>
            <div style={fieldStyle}>
              <label style={labelStyle}>Geplantes Datum *</label>
              <input
                type="datetime-local"
                value={formData.scheduledDate}
                onChange={(e) => handleChange('scheduledDate', e.target.value)}
                style={inputStyle}
              />
              {errors.scheduledDate && <span style={errorStyle}>{errors.scheduledDate}</span>}
            </div>

            <div style={fieldStyle}>
              <label style={labelStyle}>Tatsächliches Datum</label>
              <input
                type="datetime-local"
                value={formData.actualDate}
                onChange={(e) => handleChange('actualDate', e.target.value)}
                style={inputStyle}
              />
            </div>
          </div>

          <div style={rowStyle}>
            <div style={fieldStyle}>
              <label style={labelStyle}>Ort</label>
              <input
                type="text"
                value={formData.location}
                onChange={(e) => handleChange('location', e.target.value)}
                style={inputStyle}
                placeholder="Spielort"
              />
            </div>

            <div style={fieldStyle}>
              <label style={labelStyle}>Schiedsrichter</label>
              <input
                type="text"
                value={formData.referee}
                onChange={(e) => handleChange('referee', e.target.value)}
                style={inputStyle}
                placeholder="Name des Schiedsrichters"
              />
            </div>
          </div>

          <div style={rowStyle}>
            <div style={fieldStyle}>
              <label style={labelStyle}>Dauer (Minuten)</label>
              <input
                type="number"
                value={formData.duration}
                onChange={(e) => handleChange('duration', e.target.value)}
                style={inputStyle}
                placeholder="90"
                min="1"
              />
              {errors.duration && <span style={errorStyle}>{errors.duration}</span>}
            </div>

            <div style={fieldStyle}>
              <label style={labelStyle}>Status</label>
              <select
                value={formData.status}
                onChange={(e) => handleChange('status', e.target.value)}
                style={inputStyle}
              >
                <option value={LocalMatchStatus.Scheduled}>Geplant</option>
                <option value={LocalMatchStatus.Live}>Live</option>
                <option value={LocalMatchStatus.Finished}>Beendet</option>
                <option value={LocalMatchStatus.Postponed}>Verschoben</option>
                <option value={LocalMatchStatus.Cancelled}>Abgesagt</option>
              </select>
            </div>
          </div>

          <div style={fieldStyle}>
            <label style={labelStyle}>Notizen</label>
            <textarea
              value={formData.notes}
              onChange={(e) => handleChange('notes', e.target.value)}
              style={textareaStyle}
              placeholder="Zusätzliche Notizen zum Spiel..."
              rows={3}
            />
          </div>

          <div style={checkboxFieldStyle}>
            <label style={checkboxLabelStyle}>
              <input
                type="checkbox"
                checked={formData.isActive}
                onChange={(e) => handleChange('isActive', e.target.checked)}
                style={checkboxStyle}
              />
              Aktiv
            </label>
          </div>

          <div style={actionsStyle}>
            <button
              type="button"
              onClick={onCancel}
              style={cancelButtonStyle}
              disabled={loading}
            >
              Abbrechen
            </button>
            <button
              type="submit"
              style={saveButtonStyle}
              disabled={loading}
            >
              {loading ? 'Speichern...' : (match ? 'Aktualisieren' : 'Erstellen')}
            </button>
          </div>
        </form>
      </div>
    </div>
  );
};

// Styles
const overlayStyle: React.CSSProperties = {
  position: 'fixed',
  top: 0,
  left: 0,
  right: 0,
  bottom: 0,
  backgroundColor: 'rgba(0, 0, 0, 0.5)',
  display: 'flex',
  alignItems: 'center',
  justifyContent: 'center',
  zIndex: 1000,
};

const modalStyle: React.CSSProperties = {
  backgroundColor: 'white',
  borderRadius: '12px',
  maxWidth: '90vw',
  maxHeight: '90vh',
  width: '600px',
  overflow: 'hidden',
  boxShadow: '0 20px 25px -5px rgba(0, 0, 0, 0.1)',
};

const headerStyle: React.CSSProperties = {
  display: 'flex',
  justifyContent: 'space-between',
  alignItems: 'center',
  padding: '1.5rem',
  borderBottom: '1px solid #e5e7eb',
};

const closeButtonStyle: React.CSSProperties = {
  padding: '0.5rem',
  border: 'none',
  backgroundColor: 'transparent',
  cursor: 'pointer',
  fontSize: '1.5rem',
  color: '#6b7280',
};

const formStyle: React.CSSProperties = {
  padding: '1.5rem',
  maxHeight: '70vh',
  overflowY: 'auto',
};

const rowStyle: React.CSSProperties = {
  display: 'grid',
  gridTemplateColumns: '1fr 1fr',
  gap: '1rem',
  marginBottom: '1rem',
};

const fieldStyle: React.CSSProperties = {
  display: 'flex',
  flexDirection: 'column',
  gap: '0.5rem',
};

const labelStyle: React.CSSProperties = {
  fontSize: '0.9rem',
  fontWeight: '500',
  color: '#374151',
};

const inputStyle: React.CSSProperties = {
  padding: '0.75rem',
  border: '1px solid #d1d5db',
  borderRadius: '6px',
  fontSize: '1rem',
};

const textareaStyle: React.CSSProperties = {
  ...inputStyle,
  resize: 'vertical',
  minHeight: '80px',
};

const checkboxFieldStyle: React.CSSProperties = {
  marginBottom: '1.5rem',
};

const checkboxLabelStyle: React.CSSProperties = {
  display: 'flex',
  alignItems: 'center',
  gap: '0.5rem',
  fontSize: '0.9rem',
  color: '#374151',
};

const checkboxStyle: React.CSSProperties = {
  width: '1rem',
  height: '1rem',
};

const errorStyle: React.CSSProperties = {
  fontSize: '0.8rem',
  color: '#ef4444',
};

const actionsStyle: React.CSSProperties = {
  display: 'flex',
  gap: '1rem',
  justifyContent: 'flex-end',
  paddingTop: '1rem',
  borderTop: '1px solid #e5e7eb',
};

const cancelButtonStyle: React.CSSProperties = {
  padding: '0.75rem 1.5rem',
  border: '1px solid #d1d5db',
  borderRadius: '6px',
  backgroundColor: 'white',
  color: '#374151',
  cursor: 'pointer',
  fontSize: '1rem',
};

const saveButtonStyle: React.CSSProperties = {
  padding: '0.75rem 1.5rem',
  border: 'none',
  borderRadius: '6px',
  backgroundColor: '#3b82f6',
  color: 'white',
  cursor: 'pointer',
  fontSize: '1rem',
  fontWeight: '500',
};

export default LocalMatchForm;
